

































































import {
  AcoFormData,
  AdgroupFormData,
  CampaignFormData,
  FullNormalFormData,
  IdentityInfo,
  CreateMode,
  CreativeConfig,
  IDENTITY_AVAILABLE_STATUS,
  AdgroupConfig,
} from '@tt4b/ttam-enums';
import { computed, defineComponent, onMounted, PropType, ref, toRefs } from '@vue/composition-api';
import { useStdAdgroupIs, useAdgroupMode, useStore, StateEnum } from '@tt4b/ttam-hooks';
import { i18n } from '@tt4b/ttam-i18n';
import { CreateIdentityFormData, IdentityItem } from './types';
import Identity from './index';
import TtamIdentityCustomCreateCompose from './identity-create-compose.vue';
import { trackEvent } from './../../utils/index';

const name = 'ttam-identity-sales-compose';

const { Available, NOT_BUSINESS_ACCOUNT, SCOPE_UNAVAILABLE } = IDENTITY_AVAILABLE_STATUS;
const TRACK_PARAMS_ENUM = {
  [Available]: 'ttba_complete_permissions',
  [SCOPE_UNAVAILABLE]: 'ttba_incomplete_permissions',
  [NOT_BUSINESS_ACCOUNT]: 'non_ttba',
};

export default defineComponent({
  name,
  components: {
    TtamIdentitySaleTiktokList: Identity.TtamIdentitySaleTiktokList,
    TtamIdentityTiktokAccountPopover: Identity.TtamIdentityTiktokAccountPopover,
    TtamIdentityCustomList: Identity.TtamIdentityCustomList,
    TtamIdentityTypeTips: Identity.TtamIdentityTypeTips,
    TtamIdentityCustomCreateCompose,
  },
  props: {
    campaignData: {
      type: Object as PropType<CampaignFormData>,
      default: () => ({}),
    },
    adgroupData: {
      type: Object as PropType<AdgroupFormData>,
      default: () => ({}),
    },
    creativeData: {
      type: Object as PropType<FullNormalFormData & AcoFormData>,
      default: () => ({}),
    },
    creativeMode: {
      type: Boolean,
      default: false,
    },
    createMode: {
      type: String as PropType<CreateMode>,
      default: CreateMode.CREATE,
    },
    adgroupConfig: {
      type: Object as PropType<AdgroupConfig>,
      default: () => ({}),
    },
    creativeConfig: {
      type: Object as PropType<CreativeConfig>,
      default: () => ({}),
    },
    // 错误字段map，初始化后的报错信息
    errorsMap: {
      type: Object as PropType<Record<string, string>>,
      default: () => ({}),
    },
  },
  setup(props, { emit }) {
    const popoverVisible = ref(false);
    const { adgroupData, creativeMode, creativeData, campaignData, createMode, creativeConfig, adgroupConfig } =
      toRefs(props);

    // status get
    const { isLiveAds, isCLA, isStandardAdsWithCatalog, isStandardAdsWithShop } = useStdAdgroupIs({
      adgroupFormData: adgroupData,
      campaignFormData: campaignData,
    });
    const { isEdit } = useAdgroupMode({ createMode });

    // form rules
    const { identityValidator } = Identity.useIdentityRules({
      createMode,
      adgroupData,
    });

    const isNormalAds = computed(() => isStandardAdsWithCatalog.value || isCLA.value);

    // tips
    const tips = computed(() =>
      isNormalAds.value
        ? i18n.t('module_creative_identity_set_custom_desc')
        : i18n.t('module_adgroup_identity_desc_with_live'),
    );

    // form value: identity id
    const curIdentityId = computed(() =>
      creativeMode.value ? creativeData.value?.identityId : adgroupData.value?.identityId,
    );

    // live 且 creative 层级下 TikTok Identity 只可查看
    const disabledTTIdentity = computed(
      () =>
        (creativeMode.value && isLiveAds.value) ||
        isEdit.value ||
        creativeConfig.value.disableIdentity ||
        adgroupConfig.value.disableIdentity,
    );

    // TikTok 绑定用户信息获取
    const { dataSource: TikTokInfo, run: getTikTokInfo } = Identity.useTikTokAccountDataSource();
    const info = computed(() => ({
      avatar: TikTokInfo.value?.avatar,
      name: TikTokInfo.value.name,
    }));

    // custom identity get
    const {
      dataSource: customIdentity,
      originalData: originalCustomIdentity,
      isEmpty: isCustomIdentityEmpty,
      isLoading: isCustomIdentityLoading,
      run: getCustomIdentityList,
      error: isCustomIdentityError,
    } = Identity.useCustomListDataSource({ campaignData, adgroupData });

    const onCustomIdentityChange = (val: IdentityItem) => {
      const curIdentity = originalCustomIdentity.value?.find(i => i.identityId === val.id);
      emit('custom-identity-change', curIdentity);
      emit('input', curIdentity?.identityId);
    };

    // custom identity create
    const identityCreateData = ref<CreateIdentityFormData>({
      avatarIcon: {
        width: 0,
        height: 0,
        hash: '',
        webUri: '',
        url: '',
        uri: '',
      },
      displayName: '',
    });
    const customCreateVisible = ref(false);
    const onCustomIdentityAddClick = () => {
      customCreateVisible.value = true;
    };
    const { run: submitCustomIdentity, isLoading: customIdentitySubmitting } = Identity.useCustomCreateSubmit({
      modalForm: identityCreateData,
    });
    const onCustomIdentitySubmit = async (val: CreateIdentityFormData) => {
      identityCreateData.value = val;
      await submitCustomIdentity();
      await getCustomIdentityList();
      const curIdentity = originalCustomIdentity.value?.[0];
      emit('custom-identity-change', curIdentity);
      emit('input', curIdentity?.identityId);
      customCreateVisible.value = false;
    };

    const onCustomIdentityClose = () => {
      customCreateVisible.value = false;
    };

    // TikTok identity list get
    const {
      dataSource: TikTokIdentityList,
      originalData: originTikTokIdentityList,
      isEmpty: isTikTokIdentityEmpty,
      isLoading: isTikTokIdentityLoading,
      run: getTikTokIdentityList,
      hasLoaded: isTikTokIdentityListReady,
    } = Identity.useSaleTikTokListDataSource({
      adgroupData,
      campaignData,
    });

    const onTikTokIdentityChange = (data: IdentityItem) => {
      // 必需靠identityId、identityType、identityBcId一起来找，否则重名的话找得不准
      const curIdentity = originTikTokIdentityList.value?.find(
        i => i.identityId === data.id && i.identityType === data.identityType && i.identityBcId === data.identityBcId,
      );
      emit('identity-change', curIdentity);
      emit('input', curIdentity?.identityId);
    };

    onMounted(() => {
      getTikTokInfo();
    });

    const onRefresh = async () => {
      await getTikTokIdentityList();
      emit('after-refresh', originTikTokIdentityList.value);
    };

    const onAdd = () => {
      // 每次打开都更新一下状态
      getTikTokInfo();
      popoverVisible.value = true;
    };

    const linkLoading = ref(false);
    const { state } = useStore();
    const aadvid = computed(() => state[StateEnum.baseInfo].value?.aadvid);
    const { onLink, onUnLink, updateTTBA } = Identity.useTikTokAccountEvents<{
      scene: string;
    }>({
      oauthConfig: ref({
        extraParam: {
          scene: 'shopping-ads-bind',
          aadvid,
        },
      }),
      onLinkSuccess: () => {
        popoverVisible.value = false;
        onRefresh();
        getTikTokInfo();
        const availableStatus = TikTokInfo.value?.availableStatus;
        trackEvent('upgrade_identity_ttba_tab_authorize_success', {
          ttba_authority_type: TRACK_PARAMS_ENUM[availableStatus],
        });
      },
      onLinkFail: () => {
        trackEvent('upgrade_identity_ttba_tab_authorize_failed');
      },
      onUnLinkSuccess: () => {
        popoverVisible.value = false;
        onRefresh();
        getTikTokInfo();
        trackEvent('upgrade_identity_unlink_success');
      },
    });
    const popoverLink = async () => {
      try {
        linkLoading.value = true;
        await onLink();
      } catch (error) {
        console.log(error, 'link error');
      }
      linkLoading.value = false;
    };

    const onIdentityPopoverClick = async (data: IdentityInfo) => {
      const status = data.availableStatus;
      if (
        [IDENTITY_AVAILABLE_STATUS.NOT_BUSINESS_ACCOUNT, IDENTITY_AVAILABLE_STATUS.IS_PRIVATE_ACCOUNT].includes(status)
      ) {
        try {
          await updateTTBA('ttba-container');
          trackEvent('upgrade_identity_upgrade_ba_success');
          onRefresh();
        } catch (err) {
          trackEvent('upgrade_identity_upgrade_ba_failed', {
            upgrade_tt_failed_reason: err,
          });
        }
      } else if (status === IDENTITY_AVAILABLE_STATUS.SCOPE_UNAVAILABLE) {
        onLink();
      }
    };

    return {
      name,
      isEdit,
      info,
      tips,
      linkLoading,
      isNormalAds,
      customIdentity,
      curIdentityId,
      popoverVisible,
      identityValidator,
      disabledTTIdentity,
      TikTokIdentityList,
      identityCreateData,
      customCreateVisible,
      isCustomIdentityEmpty,
      isTikTokIdentityEmpty,
      isTikTokIdentityLoading,
      customIdentitySubmitting,
      onAdd,
      onLink,
      onUnLink,
      onRefresh,
      popoverLink,
      onIdentityPopoverClick,
      onTikTokIdentityChange,
      onCustomIdentityChange,
      onCustomIdentitySubmit,
      onCustomIdentityAddClick,
      onCustomIdentityClose,
      isLiveAds,
      isTikTokIdentityListReady,
    };
  },
});
